import { useRef } from 'react';
import { HOME } from '@/data/home';
import { ARTICLES } from '@/data/resources';
import { SOCIAL_URLS } from '@/data/navigation';
import { onImageError } from '@/lib/imageFallback';
import { Link } from 'react-router';
import { Reveal } from '@components/motion';
import FigmaFrame from './FigmaFrame';
import { Img } from '@/components/Img';

const TWITTER = '/landing/blog/twitter.svg';
const FACEBOOK = '/landing/blog/facebook.svg';
const INSTAGRAM = '/landing/blog/instagram.svg';
const YOUTUBE = '/landing/blog/youtube.svg';

/** The rail takes four cards — the CMS lists whichever articles hold a homepage slot first. */
const CARD_COUNT = 4;

/** Read at render, not at import: the pool is filled in by the CMS after this module loads. */
const railPosts = () => ARTICLES.slice(0, CARD_COUNT);

const SOCIALS = [
    { src: TWITTER, alt: 'X', network: 'x' },
    { src: FACEBOOK, alt: 'Facebook', network: 'facebook' },
    { src: INSTAGRAM, alt: 'Instagram', network: 'instagram' },
    { src: YOUTUBE, alt: 'YouTube', network: 'youtube' },
];

/** Card pitch on desktop: 334px image + 125px gutter. */
const CARD_W = 334;
const CARD_GAP = 125;

/** Thin diagonal arrow pointing up-right, used inside the "View All" circle. */
function ArrowUpRight({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path
                d="M9 23L23 9M23 9H12M23 9V20"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function ChevronLeft({ className, color = '#1D1B20' }: { className?: string; color?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ChevronRight({ className, color = '#1D1B20' }: { className?: string; color?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6L15 12L9 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function BlogDesktop() {
    const trackRef = useRef<HTMLDivElement>(null);
    const scrollCards = (dir: number) => {
        trackRef.current?.scrollBy({ left: dir * (CARD_W + CARD_GAP), behavior: 'smooth' });
    };

    return (
        <FigmaFrame designWidth={1920} designHeight={1100} className="w-full">
            <div className="relative h-[1100px] w-[1920px] bg-white text-[#1b1b1b]">
                {/* Heading + intro */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    className="absolute left-[310px] top-[101px] flex w-[448px] flex-col gap-[27px]"
                >
                    <p className="text-[96px] font-extrabold leading-[103px]">
                        <span className="font-normal text-[#6c6c6c]">{HOME.blog.titleLead.trim()}</span>
                        <span className="font-normal"> </span>
                        <span>{HOME.blog.titleBold}</span>
                    </p>
                    <p className="w-full text-[14px] font-normal leading-[24px] text-[#333]">{HOME.blog.blurb}</p>
                </Reveal>

                {/* View All */}
                <Reveal as="div" once variant="scale" delay={200} className="absolute left-[1557px] top-[168px]">
                    <Link to="/resources/blog" className="group flex items-center gap-[17px]">
                        <span className="flex size-[124px] shrink-0 items-center justify-center rounded-full bg-[#1b1b1b] transition-transform duration-300 group-hover:scale-[1.06]">
                            <ArrowUpRight className="size-[56px]" />
                        </span>
                        <p className="whitespace-nowrap text-[24px] font-bold leading-[32px] text-[#1b1b1b]">
                            View All
                        </p>
                    </Link>
                </Reveal>

                {/* Cards row — horizontally scrollable via the arrows below */}
                <div
                    ref={trackRef}
                    className="scrollbar-hide absolute left-[310px] top-[355px] flex w-[1610px] items-start gap-[125px] overflow-x-auto scroll-smooth"
                >
                    {railPosts().map((post, i) => (
                        <Reveal
                            key={post.slug}
                            once
                            variant="fade-left"
                            delay={i * 120}
                            className="group flex w-[334px] shrink-0 flex-col"
                        >
                            {/* Headline */}
                            <p className="line-clamp-3 h-[96px] w-[325px] text-[20px] font-semibold leading-[32px] text-black">
                                {post.title}
                            </p>

                            {/* Cover image */}
                            <div
                                className={`mt-[27px] h-[389px] w-full overflow-hidden transition-shadow duration-300 group-hover:shadow-[24px_30px_60px_-10px_rgba(0,0,0,0.28)] ${
                                    i === 0 ? 'shadow-[24px_30px_60px_-10px_rgba(0,0,0,0.28)]' : ''
                                }`}
                            >
                                <Img
                                    src={post.image}
                                    alt=""
                                    onError={onImageError(post.fallbackImage)}
                                    className="pointer-events-none size-full max-w-none object-cover"
                                />
                            </div>

                            {/* Read CTA */}
                            <Link
                                to={`/resources/blog/${post.slug}`}
                                className="mt-[36px] flex h-[44px] w-[215px] items-center justify-center self-center rounded-[8px] bg-[#1b1b1b] text-[16px] font-bold leading-[24px] text-white transition-colors duration-300 hover:bg-black"
                            >
                                Read
                            </Link>
                        </Reveal>
                    ))}
                </div>

                {/* Carousel arrows — bottom-left, under the first card */}
                <div className="absolute left-[310px] top-[1000px] flex items-center gap-[40px]">
                    <button
                        type="button"
                        onClick={() => scrollCards(-1)}
                        className="flex size-[41px] items-center justify-center"
                        aria-label="Previous posts"
                    >
                        <ChevronLeft className="size-[26px]" color="#C4C4C4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollCards(1)}
                        className="flex size-[41px] items-center justify-center"
                        aria-label="Next posts"
                    >
                        <ChevronRight className="size-[26px]" color="#1B1B1B" />
                    </button>
                </div>

                {/* Social icons */}
                <div className="absolute left-[120px] top-[761px] flex flex-col gap-[27px]">
                    {SOCIALS.map((s) =>
                        SOCIAL_URLS[s.network] ? (
                            <a
                                key={s.alt}
                                href={SOCIAL_URLS[s.network]}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={s.alt}
                                className="relative block size-[19px]"
                            >
                                <Img src={s.src} alt="" className="absolute inset-0 size-full" />
                            </a>
                        ) : null,
                    )}
                </div>
            </div>
        </FigmaFrame>
    );
}

function BlogMobile() {
    const trackRef = useRef<HTMLDivElement>(null);
    const scrollCards = (dir: number) => {
        trackRef.current?.scrollBy({ left: dir * 261, behavior: 'smooth' });
    };

    return (
        <section className="bg-white pb-12 pt-[33px] text-[#1b1b1b]">
            <div className="flex flex-col gap-2 px-5">
                <p className="text-[40px] font-extrabold leading-[50px]">
                    <span className="font-normal text-[#6c6c6c]">{HOME.blog.titleLead.trim()}</span>
                    <span className="font-normal"> </span>
                    <span>{HOME.blog.titleBold}</span>
                </p>
                <p className="text-[14px] font-normal leading-[24px] text-[#333]">{HOME.blog.blurb}</p>
            </div>

            <div
                ref={trackRef}
                className="scrollbar-hide mt-[28px] flex snap-x snap-mandatory gap-[57px] overflow-x-auto scroll-smooth px-5 pb-2"
            >
                {railPosts().map((post) => (
                    <div key={post.slug} className="w-[204px] shrink-0 snap-start">
                        <p className="line-clamp-3 text-[14px] font-semibold leading-[24px] text-black">{post.title}</p>
                        <div className="mt-[20px] h-[238px] w-full overflow-hidden">
                            <Img
                                src={post.image}
                                alt=""
                                onError={onImageError(post.fallbackImage)}
                                className="size-full object-cover"
                            />
                        </div>
                        <Link
                            to={`/resources/blog/${post.slug}`}
                            className="mt-[20px] flex h-[44px] w-full items-center justify-center rounded-[8px] bg-[#1b1b1b] text-[16px] font-bold leading-[24px] text-white"
                        >
                            Read
                        </Link>
                    </div>
                ))}
            </div>

            <div className="mt-[30px] flex items-center justify-center gap-[28px]">
                <button
                    type="button"
                    onClick={() => scrollCards(-1)}
                    className="flex size-[34px] items-center justify-center"
                    aria-label="Previous posts"
                >
                    <ChevronLeft className="size-[22px]" color="#C4C4C4" />
                </button>
                <button
                    type="button"
                    onClick={() => scrollCards(1)}
                    className="flex size-[34px] items-center justify-center"
                    aria-label="Next posts"
                >
                    <ChevronRight className="size-[22px]" color="#1B1B1B" />
                </button>
            </div>
        </section>
    );
}

export default function Blog() {
    return (
        <>
            <div className="hidden lg:block">
                <BlogDesktop />
            </div>
            <div className="lg:hidden">
                <BlogMobile />
            </div>
        </>
    );
}
